<template>
  <div class="topic-cluster-link-analysis">
    <CCard>
      <CCardHeader>
        <CRow>
          <CCol md="6">
            <strong>🔗 تحلیل خوشه‌های لینک‌دهی داخلی</strong>
            <small class="d-block text-muted">
              نمایش صفحات بر اساس سلسله‌مراتب کلمات کلیدی و روابط لینک‌دهی
            </small>
          </CCol>
          <CCol md="6" class="text-left">
            <CBadge color="info" class="mr-1">
              {{ totalClusters }} خوشه
            </CBadge>
            <CBadge color="success" class="mr-1">
              {{ totalLinkedPages }} صفحه
            </CBadge>
            <CBadge :color="getOverallHealthColor()">
              {{ getOverallHealthStatus() }}
            </CBadge>
          </CCol>
        </CRow>
      </CCardHeader>

      <CCardBody>
        <!-- فیلترها -->
        <CRow class="mb-3">
          <CCol md="4">
            <CSelect
                label="مرتب‌سازی بر اساس"
                :options="sortOptions"
                v-model="sortBy"
            />
          </CCol>
          <CCol md="4">
            <CSelect
                label="فیلتر وضعیت"
                :options="filterOptions"
                v-model="filterStatus"
            />
          </CCol>
          <CCol md="4">
            <CInput
                label="جستجوی صفحه"
                placeholder="نام صفحه را وارد کنید..."
                v-model="searchQuery"
            >
              <template #prepend>
                <CIcon name="cil-magnifying-glass" />
              </template>
            </CInput>
          </CCol>
        </CRow>

        <!-- آمار کلی -->
        <CRow class="mb-3">
          <CCol md="3">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-success">{{ correctLinks }}</div>
                <div>لینک‌های صحیح ✅</div>
                <small class="text-muted">از بیشتر به کمتر</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="3">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-danger">{{ incorrectLinks }}</div>
                <div>لینک‌های اشتباه ❌</div>
                <small class="text-muted">از کمتر به بیشتر</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="3">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-warning">{{ missingLinks }}</div>
                <div>لینک‌های از دست رفته ⚠️</div>
                <small class="text-muted">فقط پیشنهادات مرتبط</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="3">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-info">{{ totalInternalLinks }}</div>
                <div>کل لینک‌های داخلی</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- نمایش خوشه‌ها -->
        <div v-if="filteredClusters.length === 0" class="text-center py-5">
          <CIcon name="cil-info" size="xl" />
          <p class="mt-2">هیچ داده‌ای برای نمایش وجود ندارد</p>
        </div>

        <div v-else v-for="(cluster, index) in filteredClusters" :key="index" class="cluster-card mb-4">
          <CCard :class="{'border-success': cluster.health === 'healthy', 'border-danger': cluster.health === 'unhealthy', 'border-warning': cluster.health === 'warning'}">
            <CCardHeader>
              <CRow>
                <CCol md="7">
                  <h5 class="mb-0">
                    <CIcon name="cil-folder" />
                    {{ cluster.title }} ({{cluster.keyword}})
                    <CBadge :color="getHealthColor(cluster.health)" class="mr-2">
                      {{ getHealthLabel(cluster.health) }}
                    </CBadge>
                    <CBadge color="secondary">
                      {{ cluster.keywordCount }} کلمه کلیدی
                    </CBadge>
                  </h5>
                </CCol>
                <CCol md="5" class="text-left">
                  <small class="text-muted">
                    {{ cluster.linkedPages.length }} صفحه لینک‌دهنده |
                    {{ cluster.incomingLinks }} لینک ورودی |
                    {{ cluster.outgoingLinks }} لینک خروجی
                  </small>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <!-- توضیح خوشه -->
              <div class="cluster-description mb-3">
                <CBadge color="info" class="mr-1">قانون</CBadge>
                <span class="text-muted">
                  صفحات با کلمات کلیدی بیشتر ({{ cluster.keywordCount + 1 }}+ کلمه) باید به این صفحه ({{ cluster.keywordCount }} کلمه) لینک دهند
                </span>
                <span class="mr-2">
                  <CIcon :name="cluster.health === 'healthy' ? 'cil-check-circle' : 'cil-warning'"
                         :class="cluster.health === 'healthy' ? 'text-success' : 'text-warning'" />
                </span>
              </div>

              <!-- صفحات لینک‌دهنده -->
              <div v-if="cluster.linkedPages.length > 0">
                <strong>صفحات لینک‌دهنده به این خوشه:</strong>
                <CListGroup class="mt-2">
                  <CListGroupItem
                      v-for="(page, pidx) in cluster.linkedPages"
                      :key="pidx"
                      class="d-flex justify-content-between align-items-center"
                      :class="{
          'list-group-item-success': page.isCorrect && page.levelDifference === 1,
          'list-group-item-warning': page.isCorrect && page.levelDifference > 1,
          'list-group-item-danger': !page.isCorrect
        }"
                  >
                    <div>
                      <CIcon
                          :name="page.isCorrect ? (page.levelDifference === 1 ? 'cil-check' : 'cil-warning') : 'cil-x'"
                          :class="page.isCorrect ? (page.levelDifference === 1 ? 'text-success' : 'text-warning') : 'text-danger'"
                      />
                      <span class="mr-2">{{ page.title }}</span>
                      <CBadge color="secondary" class="mr-1">
                        {{ page.keywordCount }} کلمه
                      </CBadge>
                      <small class="text-muted">
                        لینک با لنگر: "{{ page.anchorText }}"
                      </small>
                    </div>
                    <div>
                      <!-- لینک صحیح با ۱ سطح پرش -->
                      <CBadge v-if="page.isCorrect && page.levelDifference === 1" color="success">
                        ✅ صحیح ({{ page.keywordCount }} → {{ cluster.keywordCount }})
                      </CBadge>

                      <!-- لینک صحیح اما با پرش بیش از ۱ سطح -->
                      <CBadge v-else-if="page.isCorrect && page.levelDifference > 1" color="warning">
                        ⚠️ پرش {{ page.levelDifference }} سطحی ({{ page.keywordCount }} → {{ cluster.keywordCount }})
                      </CBadge>

                      <!-- لینک اشتباه -->
                      <CBadge v-else color="danger">
                        ❌ اشتباه ({{ page.keywordCount }} → {{ cluster.keywordCount }})
                      </CBadge>

                      <CButton
                          v-if="!page.isCorrect || page.levelDifference > 1"
                          color="link"
                          size="sm"
                          @click="showSuggestion(page, cluster)"
                      >
                        💡 راه حل
                      </CButton>
                    </div>
                  </CListGroupItem>
                </CListGroup>
              </div>
              <!-- خطاها -->
              <div v-if="cluster.errors && cluster.errors.length > 0" class="mt-3">
                <strong class="text-danger">خطاها:</strong>
                <ul class="mt-1">
                  <li v-for="(error, eidx) in cluster.errors" :key="eidx" class="text-danger">
                    {{ error }}
                  </li>
                </ul>
              </div>

              <!-- پیشنهادات لینک‌های از دست رفته -->
              <div v-if="cluster.missingLinks && cluster.missingLinks.length > 0" class="mt-3">
                <strong class="text-warning">⚠️ لینک‌های از دست رفته (پیشنهادات مرتبط):</strong>
                <ul class="mt-1">
                  <li v-for="(missing, midx) in cluster.missingLinks" :key="midx" class="missing-link-item">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <span class="text-warning">⚠️</span>
                        <span class="mr-2">{{ missing.reason }}</span>
                        <CBadge color="info" class="mr-1">
                          لنگر: "{{ missing.suggestedAnchor }}"
                        </CBadge>
                        <CBadge color="success" class="mr-1">
                          {{ missing.relationType }}
                        </CBadge>
                      </div>
                      <CButton
                          color="primary"
                          size="sm"
                          @click="showMissingSuggestion(missing)"
                      >
                        📝 مشاهده جزئیات
                      </CButton>
                    </div>
                    <!-- نمایش کلمات مشترک -->
                    <div v-if="missing.commonWords && missing.commonWords.length > 0" class="mt-1">
                      <small class="text-muted">
                        کلمات مشترک:
                        <CBadge
                            v-for="(word, widx) in missing.commonWords"
                            :key="widx"
                            color="secondary"
                            size="sm"
                            class="mr-1"
                        >
                          {{ word }}
                        </CBadge>
                      </small>
                    </div>

                    <!-- *** پیشنهاد لینک‌سازی عملی (اضافه شده) *** -->
                    <div class="mt-2 p-2 bg-light rounded border-right border-primary">
                      <strong class="text-primary">💡 پیشنهاد لینک‌سازی:</strong>
                      <div class="mt-1">
                        <small>
                          از صفحه <strong>{{ missing.title }}</strong>
                          با لنگر <code class="text-success">"{{ missing.suggestedAnchor }}"</code>
                          به صفحه <strong>{{ missing.targetTitle }}</strong> لینک دهید.
                        </small>
                      </div>
                      <div class="mt-1">
                        <small class="text-muted">
                          مثال:
                          <span class="text-success">
                            برای اطلاعات بیشتر در مورد <a href="#" class="text-primary">{{ missing.suggestedAnchor }}</a>، مقاله جامع ما را مطالعه کنید.
                          </span>
                        </small>
                      </div>
                      <CButton
                          color="success"
                          size="sm"
                          class="mt-1"
                          @click="copyLinkSuggestion(missing)"
                      >
                        📋 کپی متن لینک
                      </CButton>
                    </div>
                  </li>
                </ul>
              </div>
            </CCardBody>
          </CCard>
        </div>
      </CCardBody>
    </CCard>

    <!-- مودال پیشنهادات -->
    <CModal
        :show.sync="showModal"
        @close="showModal = false"
        size="lg"
    >
      <CCard>
        <CCardHeader>
          💡 راه حل پیشنهادی
        </CCardHeader>
        <CCardBody>
          <div v-if="selectedSuggestion">
            <h6>مشکل:</h6>
            <p class="text-danger">{{ selectedSuggestion.problem }}</p>

            <h6>راه حل:</h6>
            <p class="text-success">{{ selectedSuggestion.solution }}</p>

            <h6>لنگر پیشنهادی:</h6>
            <div class="bg-light p-2 rounded">
              <code>{{ selectedSuggestion.suggestedAnchor }}</code>
            </div>

            <h6 class="mt-3">مثال:</h6>
            <div class="bg-info text-white p-2 rounded">
              {{ selectedSuggestion.example }}
            </div>
          </div>

        </CCardBody>
        <CCardFooter>
          <CButton color="secondary" @click="showModal = false">
            بستن
          </CButton>
        </CCardFooter>
      </CCard>

    </CModal>

    <!-- مودال پیشنهادات لینک‌های از دست رفته -->
    <CModal
        :show.sync="showMissingModal"
        @close="showMissingModal = false"
        size="lg"
    >
      <CCard>
        <CCardHeader>
          <div>📝 جزئیات لینک از دست رفته</div>
        </CCardHeader>
        <CCardBody>
          <div v-if="selectedMissing">
            <h6>صفحه مبدأ:</h6>
            <p><strong>{{ selectedMissing.title }}</strong> ({{ selectedMissing.keywordCount }} کلمه کلیدی)</p>
            <p><small class="text-muted">کلمه کلیدی: {{ selectedMissing.keyword }}</small></p>

            <h6>صفحه مقصد:</h6>
            <p><strong>{{ selectedMissing.targetTitle }}</strong> ({{ selectedMissing.targetKeywordCount }} کلمه کلیدی)</p>
            <p><small class="text-muted">کلمه کلیدی: {{ selectedMissing.targetKeyword }}</small></p>

            <h6>ارتباط موضوعی:</h6>
            <p class="text-success">{{ selectedMissing.relationType }}</p>

            <div v-if="selectedMissing.commonWords && selectedMissing.commonWords.length > 0">
              <h6>کلمات مشترک:</h6>
              <div>
                <CBadge
                    v-for="(word, widx) in selectedMissing.commonWords"
                    :key="widx"
                    color="secondary"
                    class="mr-1"
                >
                  {{ word }}
                </CBadge>
              </div>
            </div>

            <h6 class="mt-3">دلیل نیاز به لینک:</h6>
            <p class="text-warning">{{ selectedMissing.reason }}</p>

            <h6>لنگر پیشنهادی:</h6>
            <div class="bg-light p-2 rounded">
              <code>{{ selectedMissing.suggestedAnchor }}</code>
            </div>

            <h6 class="mt-3">پیشنهاد لینک‌سازی:</h6>
            <div class="bg-primary text-white p-2 rounded">
              از صفحه <strong>{{ selectedMissing.title }}</strong> با لنگر
              <code class="text-warning">"{{ selectedMissing.suggestedAnchor }}"</code>
              به صفحه <strong>{{ selectedMissing.targetTitle }}</strong> لینک دهید.
            </div>

            <h6 class="mt-3">کد HTML پیشنهادی:</h6>
            <div class="bg-dark text-white p-2 rounded">
              <code>
                &lt;a href="{{ selectedMissing.targetUrl || '#' }}"&gt;{{ selectedMissing.suggestedAnchor }}&lt;/a&gt;
              </code>
            </div>

            <h6 class="mt-3">متن پیشنهادی:</h6>
            <div class="bg-info text-white p-2 rounded">
              برای اطلاعات بیشتر در مورد <a href="#" class="text-white">{{ selectedMissing.suggestedAnchor }}</a>، مقاله جامع ما را مطالعه کنید.
            </div>
          </div>
        </CCardBody>
        <CCardFooter>
          <CButton color="secondary" @click="showMissingModal = false">
            بستن
          </CButton>
          <CButton color="primary" @click="copySuggestion">
            📋 کپی متن
          </CButton>
        </CCardFooter>
      </CCard>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'TopicClusterLinkAnalysis',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    stopWords: {
      type: Array,
      default: () => [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'روی', 'زیر', 'کنار',
        'جلوی', 'پشت', 'داخل', 'خارج', 'بالای', 'پایین', 'نزدیک',
        'دور', 'بین', 'میان', 'طریق', 'وسیلۀ', 'توسط', 'بجز', 'غیر',
        'مثل', 'مانند', 'همانند', 'چون', 'چرا', 'زیرا', 'چراکه',
        'که', 'تا', 'مگر', 'آیا', 'چه', 'چگونه', 'چطور', 'کجا',
        'کی', 'چند', 'چقدر', 'کدام', 'کدامین', 'این', 'آن', 'اینها',
        'آنها', 'ما', 'شما', 'او', 'ایشان', 'خود', 'خودی', 'یک', 'دو',
        'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'صد', 'هزار',
        'و', 'یا', 'اما', 'پس', 'پس‌از', 'پیش', 'پیش‌از', 'بعد', 'بعداز'
      ]
    }
  },
  data() {
    return {
      sortBy: 'keywordCount',
      filterStatus: 'all',
      searchQuery: '',
      showModal: false,
      showMissingModal: false,
      selectedSuggestion: null,
      selectedMissing: null,
      sortOptions: [
        { value: 'keywordCount', label: 'تعداد کلمات کلیدی (صعودی)' },
        { value: 'keywordCountDesc', label: 'تعداد کلمات کلیدی (نزولی)' },
        { value: 'health', label: 'وضعیت سلامت' },
        { value: 'links', label: 'تعداد لینک‌ها' }
      ],
      filterOptions: [
        { value: 'all', label: 'همه خوشه‌ها' },
        { value: 'healthy', label: '✅ سالم' },
        { value: 'warning', label: '⚠️ هشدار' },
        { value: 'unhealthy', label: '❌ مشکل‌دار' }
      ]
    };
  },
  computed: {
    clusters() {
      if (!this.items || this.items.length === 0) return [];

      const clusters = [];
      const pageMap = this.buildPageMap();
      const groupedPages = this.groupPagesByKeywordCount(pageMap);

      Object.keys(groupedPages).forEach(keywordCount => {
        const pages = groupedPages[keywordCount];

        pages.forEach(page => {
          const cluster = {
            title: page.title,
            keyword: page.keyword,
            keywordCount: page.keywordCount,
            health: 'healthy',
            linkedPages: [],
            errors: [],
            missingLinks: [],
            incomingLinks: 0,
            outgoingLinks: page.outgoingLinks.length,
            url: page.url
          };

          // بررسی صفحاتی که به این صفحه لینک داده‌اند
          const incomingPages = this.getIncomingPages(page, pageMap);

          incomingPages.forEach(incoming => {
            const isCorrect = incoming.keywordCount > page.keywordCount;
            const linkInfo = this.findLinkInfo(incoming, page);
            const levelDiff = incoming.keywordCount - page.keywordCount; // محاسبه تعداد سطوح پرش

            cluster.linkedPages.push({
              title: incoming.title,
              keyword: incoming.keyword,
              keywordCount: incoming.keywordCount,
              anchorText: linkInfo ? linkInfo.anchorText : 'نامشخص',
              isCorrect: isCorrect,
              url: incoming.url,
              levelDifference: levelDiff
            });

            if (isCorrect) {
              cluster.incomingLinks++;

              // اگر پرش بیش از ۱ سطح باشد، هشدار بده
              if (levelDiff > 1) {
                cluster.errors.push(
                    `⚠️ صفحه "${incoming.title}" با ${incoming.keywordCount} کلمه کلیدی به "${page.title}" با ${page.keywordCount} کلمه کلیدی لینک داده است (پرش ${levelDiff} سطحی! بهتر است از ${incoming.keywordCount} به ${page.keywordCount + 1} کلمه لینک دهید)`
                );
                if (cluster.health !== 'unhealthy') {
                  cluster.health = 'warning';
                }
              }
            } else {
              cluster.errors.push(
                  `❌ صفحه "${incoming.title}" با ${incoming.keywordCount} کلمه کلیدی به "${page.title}" با ${page.keywordCount} کلمه کلیدی لینک داده است (اشتباه: باید از بیشتر به کمتر باشد)`
              );
              cluster.health = 'unhealthy';
            }
          });

          // پیدا کردن لینک‌های از دست رفته با بررسی ارتباط موضوعی
          const missingLinks = this.findMissingLinks(page, pageMap);
          missingLinks.forEach(missing => {
            cluster.missingLinks.push(missing);
            if (cluster.health !== 'unhealthy') {
              cluster.health = 'warning';
            }
          });

          // بررسی وضعیت سلامت صفحه بر اساس لینک‌های ورودی و خروجی
          if (cluster.incomingLinks === 0 && cluster.outgoingLinks === 0) {
            cluster.health = 'unhealthy';
            cluster.errors.push(
                `🚨 صفحه "${cluster.title}" کاملاً یتیم است! هیچ لینک ورودی یا خروجی ندارد.`,
                `💡 پیشنهاد: حداقل یک لینک از صفحه اصلی یا مقالات دیگر به این صفحه اضافه کنید تا گوگل آن را کشف کند.`
            );
          } else if (cluster.incomingLinks === 0 && cluster.outgoingLinks > 0) {
            cluster.health = 'unhealthy';
            cluster.errors.push(
                `❌ صفحه "${cluster.title}" یتیم است! هیچ لینک ورودی ندارد (${cluster.outgoingLinks} لینک خروجی دارد).`,
                `💡 پیشنهاد: از صفحات دیگر به این صفحه لینک دهید تا گوگل آن را کشف کند و ارزش دریافت کند.`
            );
          } else if (cluster.incomingLinks > 0 && cluster.outgoingLinks === 0) {
            if (cluster.health !== 'unhealthy') {
              cluster.health = 'warning';
            }
            cluster.errors.push(
                `⚠️ صفحه "${cluster.title}" فقط لینک ورودی دارد (${cluster.incomingLinks} لینک) و هیچ لینک خروجی ندارد.`,
                `💡 پیشنهاد: از این صفحه به صفحات مرتبط دیگر لینک دهید تا ارزش (PageRank) را به صفحات دیگر منتقل کنید.`
            );
          } else if (cluster.incomingLinks > 0 && cluster.outgoingLinks > 0) {
            if (cluster.errors.length === 0) {
              cluster.health = 'healthy';
            }
          }

          clusters.push(cluster);
        });
      });

      return clusters;
    },
    filteredClusters() {
      let filtered = this.clusters;

      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(c => c.health === this.filterStatus);
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(c =>
            c.title.toLowerCase().includes(query) ||
            c.linkedPages.some(p => p.title.toLowerCase().includes(query))
        );
      }

      switch (this.sortBy) {
        case 'keywordCount':
          filtered.sort((a, b) => a.keywordCount - b.keywordCount);
          break;
        case 'keywordCountDesc':
          filtered.sort((a, b) => b.keywordCount - a.keywordCount);
          break;
        case 'health':
          const healthOrder = {healthy: 1, warning: 2, unhealthy: 3};
          filtered.sort((a, b) => healthOrder[a.health] - healthOrder[b.health]);
          break;
        case 'links':
          filtered.sort((a, b) => (b.incomingLinks + b.outgoingLinks) - (a.incomingLinks + a.outgoingLinks));
          break;
      }

      return filtered;
    },

    correctLinks() {
      let count = 0;
      this.clusters.forEach(c => {
        c.linkedPages.forEach(p => {
          if (p.isCorrect) count++;
        });
      });
      return count;
    },

    incorrectLinks() {
      let count = 0;
      this.clusters.forEach(c => {
        c.linkedPages.forEach(p => {
          if (!p.isCorrect) count++;
        });
      });
      return count;
    },

    missingLinks() {
      let count = 0;
      this.clusters.forEach(c => {
        count += c.missingLinks.length;
      });
      return count;
    },

    totalInternalLinks() {
      let count = 0;
      this.clusters.forEach(c => {
        count += c.incomingLinks + c.outgoingLinks;
      });
      return count;
    },

    totalClusters() {
      return this.clusters.length;
    },

    totalLinkedPages() {
      let count = 0;
      this.clusters.forEach(c => {
        count += c.linkedPages.length;
      });
      return count;
    }
  },
  methods: {
    buildPageMap() {
      const map = {};

      this.items.forEach(item => {
        const keywordCount = this.countKeywords(item.keyword || '');
        map[item.url] = {
          title: item.title,
          url: item.url,
          keyword: item.keyword,
          keywordCount: keywordCount,
          htmlContent: item.htmlContent,
          outgoingLinks: this.extractOutgoingLinks(item.htmlContent)
        };
      });

      return map;
    },

    extractOutgoingLinks(htmlContent) {
      if (!htmlContent) return [];

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const links = [];

      doc.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href');
        const anchorText = link.textContent.trim();
        if (href && href.includes('/')) {
          links.push({
            url: href,
            anchorText: anchorText
          });
        }
      });

      return links;
    },

    groupPagesByKeywordCount(pageMap) {
      const grouped = {};

      Object.values(pageMap).forEach(page => {
        const count = page.keywordCount;
        if (!grouped[count]) {
          grouped[count] = [];
        }
        grouped[count].push(page);
      });

      return grouped;
    },

    getIncomingPages(page, pageMap) {
      const incoming = [];

      Object.values(pageMap).forEach(p => {
        if (p.url === page.url) return;

        const hasLink = p.outgoingLinks.some(link => {
          return link.url === page.url || link.url.includes(page.url);
        });

        if (hasLink) {
          incoming.push(p);
        }
      });

      return incoming;
    },

    findLinkInfo(fromPage, toPage) {
      const link = fromPage.outgoingLinks.find(l => {
        return l.url === toPage.url || l.url.includes(toPage.url);
      });
      return link || null;
    },

    findMissingLinks(page, pageMap) {
      const missing = [];
      const targetKeywordCount = page.keywordCount + 1;

      Object.values(pageMap).forEach(p => {
        if (p.url === page.url) return;

        if (p.keywordCount === targetKeywordCount) {
          const hasLink = p.outgoingLinks.some(link => {
            return link.url === page.url || link.url.includes(page.url);
          });

          if (!hasLink) {
            // بررسی ارتباط موضوعی
            const relationResult = this.checkThematicRelation(p.keyword, page.keyword);

            // فقط اگر مرتبط بودند، پیشنهاد بده
            if (relationResult.isRelated) {
              missing.push({
                title: p.title,
                keyword: p.keyword,
                keywordCount: p.keywordCount,
                suggestedAnchor: page.title,
                targetTitle: page.title,
                targetKeyword: page.keyword,
                targetKeywordCount: page.keywordCount,
                targetUrl: page.url,
                commonWords: relationResult.commonWords,
                relationType: relationResult.type,
                reason: `صفحه "${p.title}" (${p.keyword}) با ${p.keywordCount} کلمه کلیدی باید به "${page.title}" با ${page.keywordCount} کلمه کلیدی لینک دهد`
              });
            }
          }
        }
      });

      return missing;
    },

    // تابع بررسی ارتباط موضوعی - همه کلمات صفحه مقصد باید در صفحه مبدأ باشد
    checkThematicRelation(keyword1, keyword2) {
      if (!keyword1 || !keyword2) {
        return {isRelated: false, commonWords: [], type: 'بدون ارتباط'};
      }

      // حذف کلمات ربط
      const clean1 = this.removeStopWords(keyword1).join(' ');
      const clean2 = this.removeStopWords(keyword2).join(' ');

      if (!clean1 || !clean2) {
        return {isRelated: false, commonWords: [], type: 'بدون کلمه کلیدی معنی‌دار'};
      }

      const words1 = clean1.split(' ');
      const words2 = clean2.split(' ');

      // شرط: صفحه مبدأ باید همه کلمات صفحه مقصد را داشته باشد
      const allWordsExist = words2.every(word => words1.includes(word));

      if (allWordsExist) {
        // پیدا کردن کلمات مشترک
        const commonWords = words2.filter(word => words1.includes(word));

        return {
          isRelated: true,
          commonWords: commonWords,
          type: `✅ مرتبط (همه ${commonWords.length} کلمه مشترک: ${commonWords.join('، ')})`
        };
      }

      return {
        isRelated: false,
        commonWords: [],
        type: '❌ بدون ارتباط کامل'
      };
    },

    removeStopWords(text) {
      if (!text) return [];

      const words = text.split(/\s+/).filter(w => w.length > 0);

      return words.filter(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(cleanWord) && cleanWord.length > 0;
      });
    },

    countKeywords(text) {
      if (!text) return 0;

      const words = text.split(/\s+/).filter(w => w.length > 0);

      const filtered = words.filter(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(cleanWord) && cleanWord.length > 0;
      });

      return filtered.length;
    },
    showSuggestion(page, cluster) {
      let problem = '';
      let solution = '';

      if (!page.isCorrect) {
        problem = `صفحه "${page.title}" با ${page.keywordCount} کلمه کلیدی به "${cluster.title}" با ${page.keywordCount} کلمه کلیدی لینک داده است.`;
        solution = `برای بهبود سئو، صفحات با کلمات کلیدی بیشتر (${page.keywordCount} کلمه) باید به صفحات با کلمات کلیدی کمتر (${cluster.keywordCount} کلمه) لینک دهند.`;
      } else if (page.levelDifference > 1) {
        problem = `صفحه "${page.title}" با ${page.keywordCount} کلمه کلیدی به "${cluster.title}" با ${cluster.keywordCount} کلمه کلیدی لینک داده است (پرش ${page.levelDifference} سطحی!).`;
        solution = `بهتر است از یک مسیر لایه‌ای استفاده کنید:
${page.keywordCount} کلمه → ${page.keywordCount - 1} کلمه → ... → ${cluster.keywordCount} کلمه

مثال:
"${page.title}" (${page.keywordCount} کلمه)
→ با لنگر "${this.getIntermediateAnchor(page.keyword, cluster.keyword)}"
→ به صفحه "${this.getIntermediatePage(page, cluster)}" (${page.keywordCount - 1} کلمه) لینک دهید

سپس آن صفحه به "${cluster.title}" (${cluster.keywordCount} کلمه) لینک دهد.`;
      }

      this.selectedSuggestion = {
        problem: problem,
        solution: solution,
        suggestedAnchor: cluster.title,
        example: `مثال: <a href="${cluster.url}">${cluster.title}</a>`
      };
      this.showModal = true;
    },
// تابع کمکی برای پیدا کردن لنگر میانی
    getIntermediateAnchor(fromKeyword, toKeyword) {
      const words1 = this.removeStopWords(fromKeyword);
      const words2 = this.removeStopWords(toKeyword);

      // کلمات مشترک را پیدا کن
      const commonWords = words1.filter(w => words2.includes(w));

      // اگر کلمات مشترک داشت، از آنها استفاده کن
      if (commonWords.length > 0) {
        return commonWords.join(' ');
      }

      // اگر نه، از کلمه کلیدی صفحه مقصد استفاده کن
      return toKeyword;
    },

// تابع کمکی برای پیدا کردن صفحه میانی
    getIntermediatePage(page, cluster) {
      // این تابع باید صفحه‌ای با keywordCount = page.keywordCount - 1 پیدا کند
      // که به cluster لینک داده باشد
      // برای ساده‌سازی، فعلاً یک پیام نمایش می‌دهیم
      return `صفحه با ${page.keywordCount - 1} کلمه کلیدی مرتبط`;
    },
    showMissingSuggestion(missing) {
      this.selectedMissing = missing;
      this.showMissingModal = true;
    },

    copySuggestion() {
      if (!this.selectedMissing) return;

      const text = `برای اطلاعات بیشتر در مورد ${this.selectedMissing.suggestedAnchor}، مقاله جامع ما را مطالعه کنید.`;

      navigator.clipboard.writeText(text).then(() => {
        alert('✅ متن کپی شد!');
      }).catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('✅ متن کپی شد!');
      });
    },

    // *** تابع جدید: کپی متن لینک پیشنهادی ***
    copyLinkSuggestion(missing) {
      const text = `برای اطلاعات بیشتر در مورد ${missing.suggestedAnchor}، مقاله جامع ما را مطالعه کنید.`;

      navigator.clipboard.writeText(text).then(() => {
        alert('✅ متن لینک کپی شد!');
      }).catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('✅ متن لینک کپی شد!');
      });
    },

    getHealthColor(health) {
      const colors = {
        healthy: 'success',
        warning: 'warning',
        unhealthy: 'danger'
      };
      return colors[health] || 'secondary';
    },

    getHealthLabel(health) {
      const labels = {
        healthy: '✅ سالم',
        warning: '⚠️ هشدار',
        unhealthy: '❌ مشکل‌دار'
      };
      return labels[health] || health;
    },

    getOverallHealthColor() {
      const healthy = this.clusters.filter(c => c.health === 'healthy').length;
      const warning = this.clusters.filter(c => c.health === 'warning').length;
      const unhealthy = this.clusters.filter(c => c.health === 'unhealthy').length;

      if (unhealthy > 0) return 'danger';
      if (warning > 0) return 'warning';
      return 'success';
    },

    getOverallHealthStatus() {
      const healthy = this.clusters.filter(c => c.health === 'healthy').length;
      const warning = this.clusters.filter(c => c.health === 'warning').length;
      const unhealthy = this.clusters.filter(c => c.health === 'unhealthy').length;

      if (unhealthy > 0) return `❌ ${unhealthy} مشکل`;
      if (warning > 0) return `⚠️ ${warning} هشدار`;
      return `✅ ${healthy} سالم`;
    }
  }
};
</script>

<style scoped>
.topic-cluster-link-analysis {
  direction: rtl;
  text-align: right;
}

.cluster-card {
  transition: all 0.3s ease;
}

.cluster-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.text-value-lg {
  font-size: 2rem;
  font-weight: bold;
}

.border-success {
  border: 2px solid #28a745 !important;
}

.border-danger {
  border: 2px solid #dc3545 !important;
}

.border-warning {
  border: 2px solid #ffc107 !important;
}

.list-group-item-success {
  background-color: #d4edda;
}

.list-group-item-danger {
  background-color: #f8d7da;
}

.missing-link-item {
  background-color: #fff3cd;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 5px;
  border-right: 3px solid #ffc107;
}

.mr-1 {
  margin-left: 0.25rem !important;
}

.mr-2 {
  margin-left: 0.5rem !important;
}

.text-left {
  text-align: left !important;
}

.cluster-description {
  background-color: #f8f9fa;
  padding: 8px 12px;
  border-radius: 4px;
  border-right: 3px solid #0d6efd;
}
</style>